/**
 * Оболочка приложения: сайдбар, навигация и защита несохранённых изменений.
 *
 * Здесь живёт правило §6, применённое ко ВСЕМ разделам сразу:
 *   - точка у пункта меню, если в разделе есть несохранённые правки;
 *   - перехват перехода в другой раздел с тремя вариантами;
 *   - перехват закрытия вкладки через `beforeunload`.
 *
 * В демо это был единственный `pendingSection` в корневом компоненте. Здесь
 * то же самое, но поверх роутера: переход перехватывается до смены адреса,
 * иначе пользователь увидел бы уже другой раздел под модальным окном.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ClipboardList,
  FileText,
  LogOut,
  Menu,
  X,
  Settings as SettingsIcon,
  Users,
  Wallet,
} from 'lucide-react';
import type { ModuleName } from '@otc/shared';
import { anyModuleDirty, useDrafts } from '../store/drafts.js';
import { useExtraDrafts } from '../store/extraDrafts.js';
import { MODULE_LABELS, useModule } from '../store/useModule.js';
import { UnsavedChangesModal } from '../components/GuardModals.js';
import { ClinicIcon, ConstructionIcon, DoctorIcon, OrthoTechLogo } from '../components/icons.js';
import type { CurrentUser } from '../api/queries.js';
import { useCloseOnBack } from '../components/useCloseOnBack.js';

/**
 * Тип иконки — простая сигнатура функции, а не `ComponentType`.
 *
 * Иконки lucide-react объявлены как `ForwardRefExoticComponent` со своими
 * `propTypes`, а собственная `ConstructionIcon` — обычная функция. Сравнение через
 * `ComponentType` упирается в несовместимость `propTypes`; сравнение по
 * сигнатуре вызова проходит для обоих. `size` допускает и строку — так его
 * объявляет lucide.
 */
type IconComponent = (props: { size?: number | string }) => ReactNode;

interface NavItem {
  path: string;
  label: string;
  icon: IconComponent;
  module: ModuleName;
  /** Пункт виден только владельцу (§7). */
  ownerOnly?: boolean;
  /**
   * Пункт для СОТРУДНИКА, которому открыли просмотр своей зарплаты.
   *
   * Владельцу он не нужен: у него есть «Сотрудники» со всеми расчётами
   * сразу, и второй раздел про то же только путал бы.
   */
  ownSalaryOnly?: boolean;
}

/** Порядок пунктов повторяет демо: он привычен лаборатории. */
const NAV: NavItem[] = [
  { path: '/orders', label: 'Заказы', icon: ClipboardList, module: 'orders' },
  // Человек со значком зуба, а не группа людей: «Врачи» и «Сотрудники» не
  // должны путаться. Тот же значок стоит на карточке клиники у списка врачей.
  { path: '/doctors', label: 'Врачи', icon: DoctorIcon, module: 'doctors' },
  { path: '/clinics', label: 'Клиники', icon: ClinicIcon, module: 'clinics' },
  { path: '/constructions', label: 'Конструкции', icon: ConstructionIcon, module: 'constructions' },
  { path: '/documents', label: 'Документы', icon: FileText, module: 'templates' },
  // Только владельцу — как и «Финансы»: ставки, премии и логины коллег
  // сотруднику видеть незачем.
  { path: '/employees', label: 'Сотрудники', icon: Users, module: 'employees', ownerOnly: true },
  // Только владельцу: в финансы лаборатории не заходит никто, кроме него.
  { path: '/finance', label: 'Финансы', icon: Wallet, module: 'finance', ownerOnly: true },
  {
    path: '/my-salary',
    label: 'Моя зарплата',
    icon: Wallet,
    module: 'employees',
    ownSalaryOnly: true,
  },
];

/** Раздел, которому принадлежит текущий адрес. */
function moduleOfPath(pathname: string): ModuleName | null {
  if (pathname.startsWith('/settings')) return 'settings';
  const item = NAV.find((n) => pathname.startsWith(n.path));
  return item?.module ?? null;
}

export function Shell({ user, onLogout, children }: { user: CurrentUser; onLogout: () => void; children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();

  const currentModule = moduleOfPath(location.pathname);
  const [pendingPath, setPendingPath] = useState<string | null>(null);
  const [leavingSaving, setLeavingSaving] = useState(false);

  // Пересчёт точек-индикаторов при любом изменении черновиков.
  const modules = useDrafts((state) => state.modules);
  const extraDrafts = useExtraDrafts(state => state.entries);
  const isDirty = (module: ModuleName) => {
    void modules;
    return useDrafts.getState().isDirty(module) || Boolean(extraDrafts[module]);
  };

  const guard = useModule(currentModule ?? 'orders');

  /**
   * Перехват закрытия вкладки.
   *
   * Браузер показывает своё стандартное окно — свой текст задать нельзя.
   * Но без этого обработчика правки просто исчезнут при случайном Ctrl+W.
   */
  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!anyModuleDirty() && !Object.keys(useExtraDrafts.getState().entries).length) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  const go = (path: string) => {
    if (path === location.pathname) return;
    if (currentModule && isDirty(currentModule)) {
      setPendingPath(path);
      return;
    }
    navigate(path);
  };

  const leaveWithSave = async () => {
    setLeavingSaving(true);
    try {
    const outcome = await guard.save();
    if (!outcome.ok) {
      // Сохранить не удалось — остаёмся на месте, чтобы пользователь
      // разобрался с конфликтом или отказом, а не потерял правки при переходе.
      setPendingPath(null);
      return;
    }
    const extra = currentModule ? useExtraDrafts.getState().entries[currentModule] : undefined;
    if (extra && !await extra.save()) { setPendingPath(null); return; }
    const target = pendingPath;
    setPendingPath(null);
    if (target) navigate(target);
    } finally { setLeavingSaving(false); }
  };

  const leaveWithoutSave = () => {
    guard.discard();
    if (currentModule) useExtraDrafts.getState().entries[currentModule]?.discard();
    const target = pendingPath;
    setPendingPath(null);
    if (target) navigate(target);
  };

  const visibleNav = NAV.filter((item) => {
    if (item.ownerOnly && user.role !== 'OWNER') return false;
    // Свою зарплату смотрит сотрудник, которому её открыли, — и только он.
    if (item.ownSalaryOnly && (user.role === 'OWNER' || !user.salaryAccess)) return false;
    return true;
  });

  /*
   * Боковое меню на узком экране УЕЗЖАЕТ за край.
   *
   * Оно шириной 224 пикселя, а телефон — около 390: меню занимало больше
   * половины, содержимому оставалась полоска, в которую не помещалась ни одна
   * таблица. На экране от 768 пикселей меню стоит как стояло.
   */
  const [menuOpen, setMenuOpen] = useState(false);

  // «Назад» на телефоне закрывает меню, а не выходит из программы.
  useCloseOnBack(menuOpen, () => setMenuOpen(false));
  /*
   * Открытый раздел нужен ЦЕЛИКОМ, а не одной подписью: в шапке на телефоне
   * рядом с названием стоит его значок — тот же, что в меню.
   *
   * На широком экране раздел виден по подсветке в боковом меню, а на
   * телефоне меню спрятано, и опознать страницу можно только по надписи.
   * Значок узнаётся быстрее слова — по нему же ищут раздел в меню.
   */
  const настройки = location.pathname.startsWith('/settings');
  const currentItem = NAV.find((item) => location.pathname.startsWith(item.path));
  const currentLabel = currentItem?.label ?? (настройки ? 'Настройки' : '');
  const CurrentIcon = currentItem?.icon ?? (настройки ? SettingsIcon : null);

  /**
   * Прокручивается ОКНО, а не внутренний контейнер.
   *
   * Раньше боковое меню и содержимое лежали в блоке высотой в экран, а
   * прокрутку брал на себя `main`. Внешне это выглядит похоже, но ведёт себя
   * иначе: у окна нет полосы прокрутки, не работают PageDown, Home и End,
   * а колесо мыши крутит страницу только когда указатель точно над областью
   * содержимого. В разделах с длинными списками — «Клиники», «Врачи»,
   * «Конструкции» — это читается как сломанная прокрутка.
   *
   * Меню закреплено `fixed` и остаётся на месте, содержимое отступает на его
   * ширину и прокручивается штатными средствами браузера.
   */
  /** Переход с закрытием ящика: на телефоне меню перекрывает содержимое. */
  const goAndClose = (path: string) => {
    setMenuOpen(false);
    go(path);
  };

  return (
    <div>
      {/*
        Верхняя полоса — ТОЛЬКО на узком экране: там нет бокового меню.

        Слой ВЫШЕ ящика (z-40 против z-30). Раньше оба лежали на z-30, ящик
        шёл в разметке позже и перекрывал полосу целиком — вместе с той самой
        кнопкой, которой меню закрывают. Открыв меню, закрыть его кнопкой было
        нельзя: палец попадал в ящик. Оставался только тычок мимо, о котором
        никто не догадывается.
      */}
      <header
        className="fixed inset-x-0 top-0 z-40 flex h-14 items-center gap-3 px-3 md:hidden"
        style={{ background: 'var(--otc-accent-dark)' }}
      >
        <button
          onClick={() => setMenuOpen((v) => !v)}
          className="rounded-lg p-2"
          style={{ color: '#fff' }}
          aria-label={menuOpen ? 'Закрыть меню' : 'Открыть меню'}
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
        <span className="flex min-w-0 items-center gap-2 text-white">
          {/* Значок наследует белый цвет шапки: у всех значков обводка
              задана текущим цветом. */}
          {CurrentIcon && (
            <span className="flex-shrink-0" aria-hidden>
              <CurrentIcon size={20} />
            </span>
          )}
          <span className="min-w-0 truncate font-display text-base font-bold">{currentLabel}</span>
        </span>
        {(anyModuleDirty() || Object.keys(extraDrafts).length > 0) && (
          <span className="ml-auto text-[11px]" style={{ color: 'rgba(255,255,255,0.7)' }}>
            есть несохранённое
          </span>
        )}
      </header>

      {/* Затемнение под открытым меню: щелчок мимо закрывает. */}
      {menuOpen && (
        <button
          onClick={() => setMenuOpen(false)}
          className="fixed inset-0 z-20 bg-black/40 md:hidden"
          aria-label="Закрыть меню"
        />
      )}

      <aside
        className={[
          // На телефоне ящик начинается под полосой: она остаётся видна и
          // рабочей. На широком экране полосы нет, и ящик идёт во всю высоту.
          'fixed bottom-0 top-14 left-0 z-30 flex w-56 flex-col px-3 py-4 transition-transform duration-200',
          'md:top-0 md:py-5',
          menuOpen ? 'translate-x-0' : '-translate-x-full',
          'md:translate-x-0',
        ].join(' ')}
        style={{ background: 'var(--otc-accent-dark)' }}
      >
        <div className="mb-6 flex items-center gap-2.5 px-2">
          {/* Крупнее прежнего: у логотипа тонкие проволочные элементы,
              на мелком размере они сливаются в пятно. */}
          <OrthoTechLogo size={46} />
          <div className="min-w-0">
            <p className="font-display text-[17px] font-bold leading-tight text-white">OrthoTechLab</p>
            <p className="truncate text-[11px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
              {user.name}
            </p>
          </div>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto">
          {visibleNav.map((item) => (
            <NavButton
              key={item.path}
              item={item}
              active={location.pathname.startsWith(item.path)}
              dirty={isDirty(item.module)}
              onClick={() => goAndClose(item.path)}
            />
          ))}
        </nav>

        <button
          onClick={() => goAndClose('/settings')}
          className={`otc-nav mb-2 flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm ${
            location.pathname.startsWith('/settings') ? 'otc-nav-active' : ''
          }`}
          aria-current={location.pathname.startsWith('/settings') ? 'page' : undefined}
        >
          <span className="otc-nav-icon">
            <SettingsIcon size={22} />
          </span>
          Настройки
          {isDirty('settings') && <DirtyDot />}
        </button>

        <div className="border-t px-2 pt-2" style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
          <button
            onClick={onLogout}
            className="otc-nav flex w-full items-center gap-2 rounded-lg px-1 py-1.5 text-xs"
          >
            <span className="otc-nav-icon">
              <LogOut size={16} />
            </span>
            Выйти
          </button>
        </div>
      </aside>

      {/* `min-w-0` обязателен: без него широкая таблица растягивает блок
          и выталкивает содержимое за правый край вместо своей прокрутки. */}
      {/*
        Отступ слева — только там, где есть боковое меню. Сверху на узком
        экране освобождается место под полосу с кнопкой меню.
      */}
      {/*
        Поля на телефоне узкие намеренно: каждый лишний пиксель отступа —
        это пиксель, отнятый у строки заказа, которая и без того переносится.
      */}
      {/*
        `overflow-x: clip` — страховка от «уехавшей вбок страницы».

        Стоит одному вложенному блоку оказаться шире экрана — длинной строке
        итогов, широкой таблице без своего контейнера, — и вбок разъезжается
        ВЕСЬ документ: заголовок, меню и карточки уходят за левый край, а
        вернуть их можно только прокруткой. Ошибка в одном месте ломает вид
        всей страницы, и по внешнему виду не понять, где именно.

        `clip`, а не `hidden`: последний делает элемент прокручиваемым
        контейнером, и прилипающие шапки таблиц перестают прилипать. Свои
        `overflow-x: auto` внутри — у таблиц и полосы вкладок — продолжают
        прокручиваться как прежде.
      */}
      {/*
        Карточка заказа содержимое НЕ сдвигает: с 18.09.2026 это подвижное
        окно поверх журнала (см. `OrderPanel`). Прежде она раздвигала
        страницу переменной `--otc-dock`, и таблица ужималась до трёх
        столбцов; теперь журнал остаётся целым, а окно отодвигают мышью.
      */}
      <main
        className="min-w-0 overflow-x-clip p-2.5 pt-[4.25rem] md:ml-56 md:p-8"
      >{children}</main>

      {pendingPath && currentModule && (
        <UnsavedChangesModal
          moduleLabel={MODULE_LABELS[currentModule]}
          saving={guard.saving || leavingSaving}
          onSaveAndLeave={() => void leaveWithSave()}
          onDiscardAndLeave={leaveWithoutSave}
          onStay={() => setPendingPath(null)}
        />
      )}
    </div>
  );
}

function NavButton({
  item,
  active,
  dirty,
  onClick,
}: {
  item: NavItem;
  active: boolean;
  dirty: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      // Цвета и отклик — в `otc-nav` (index.css). По месту их задавать нельзя:
      // встроенные стили перебивают наведение, и подсветка не срабатывает.
      className={`otc-nav flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm ${
        active ? 'otc-nav-active' : ''
      }`}
      aria-current={active ? 'page' : undefined}
    >
      {/* 22 px, а не 16: в 16 рисунок дуги в «Конструкциях» слипался. */}
      <span className="otc-nav-icon">
        <item.icon size={22} />
      </span>
      {item.label}
      {dirty && <DirtyDot />}
    </button>
  );
}

/** Точка «есть несохранённые правки» — тем же янтарным цветом, что в демо. */
function DirtyDot() {
  return (
    <span
      className="ml-auto h-1.5 w-1.5 flex-shrink-0 rounded-full"
      style={{ background: 'var(--otc-amber)' }}
      title="Есть несохранённые изменения"
    />
  );
}
